import {
  faAdd,
  faCheck,
  faPen,
  faPrint,
  faQrcode,
  faUsers,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataTable } from "@ventumsoftware/react-toolkit";
import { type FC, useMemo, useState } from "react";
import { useApi, useFetchedResource } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import type { Company, Project } from "../../api/dto";
import type { ProjectCheckIn } from "../../api/dto/ProjectCheckIn";
import { CheckInCodeModal } from "../../modals/CheckInCodeModal";
import { CheckInVisitorsModal } from "../../modals/CheckInVisitorsModal";
import { CloseCheckInModal } from "../../modals/CloseCheckInModal";
import { EditCheckInModal } from "../../modals/EditCheckInModal";
import { PrintCheckInCodeModal } from "../../modals/PrintCheckInCodeModal";
import { StartCheckInModal } from "../../modals/StartCheckInModal";
import { usePermissions } from "../../permissions/usePermissions";
import { formatDateTime } from "../../util/formatTime";
import { ContextMenu } from "../content/ContextMenu";
import { HoverHint } from "../content/HoverHint";
import { SectionHeader } from "../content/SectionHeader";
import { Badge, BadgeStyle } from "../data/Badge";
import { TablePlaceholder } from "../data/TablePlaceholder";
import { Button } from "../form/Button";
import { useModal } from "../layout/ModalProvider";
import { useRefreshEffect } from "../RefreshController";
import { checkInPermissionOptions, checkInQuestionSetOptions } from "./checkInPermissionLabels";

const CheckInActions: FC<{ checkIn: ProjectCheckIn; projectName: string }> = (props) => {
  const codeModal = useModal({
    title: `Code voor ${props.projectName}`,
    body: <CheckInCodeModal checkIn={props.checkIn} />,
  });
  const printModal = useModal({
    title: `Code printen voor ${props.projectName}`,
    body: <PrintCheckInCodeModal checkIn={props.checkIn} projectName={props.projectName} />,
  });
  const visitorsModal = useModal({
    title: `Bezoekers van ${props.projectName}`,
    body: <CheckInVisitorsModal checkIn={props.checkIn} />,
    size: "lg",
  });
  const editModal = useModal({
    title: `Check-in bewerken`,
    body: <EditCheckInModal checkIn={props.checkIn} projectName={props.projectName} />,
  });
  const closeModal = useModal({
    title: "Check-in sluiten",
    body: <CloseCheckInModal checkIn={props.checkIn} projectName={props.projectName} />,
  });
  return (
    <div className={"flex justify-end"}>
      <ContextMenu
        options={[
          ...(props.checkIn.status !== "closed"
            ? [
                { icon: faQrcode, label: "Code bekijken", onClick: () => codeModal.open() },
                ...(props.checkIn.status === "active"
                  ? [{ icon: faPrint, label: "Code printen", onClick: () => printModal.open() }]
                  : []),
              ]
            : []),
          { icon: faUsers, label: "Bezoekers", onClick: () => visitorsModal.open() },
          ...(props.checkIn.status !== "closed"
            ? [
                { icon: faPen, label: "Bewerken", onClick: () => editModal.open() },
                { icon: faXmark, label: "Sluiten", onClick: () => closeModal.open(), danger: true },
              ]
            : []),
        ]}
      />
    </div>
  );
};

/** "Verlopen" once the grant period of the check-in is over, "Ingetrokken" when it was closed explicitly. */
const StatusBadge: FC<{ checkIn: ProjectCheckIn }> = ({ checkIn }) => {
  if (checkIn.status === "closed" && checkIn.closedAt !== null) {
    return (
      <HoverHint hint={`Ingetrokken op ${formatDateTime(checkIn.closedAt)}`}>
        <Badge style={BadgeStyle.dark}>Ingetrokken</Badge>
      </HoverHint>
    );
  }
  if (checkIn.status === "expired") {
    return (
      <HoverHint hint={`Verlopen op ${formatDateTime(checkIn.expiresAt)}`}>
        <Badge style={BadgeStyle.danger}>Verlopen</Badge>
      </HoverHint>
    );
  }
  return null;
};

const HintList: FC<{ title: string; items: string[]; emptyText?: string }> = (props) => (
  <div>
    <div className={"text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1"}>
      {props.title}
    </div>
    {props.items.length === 0 ? (
      <div className={"text-slate-400"}>{props.emptyText}</div>
    ) : (
      <ul className={"space-y-1"}>
        {props.items.map((item) => (
          <li key={item} className={"flex items-start"}>
            <FontAwesomeIcon icon={faCheck} className={"w-3 mr-2 mt-0.5 text-emerald-400"} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

/** "3 rechten"; hovering shows which rights and question sets they are. */
const PermissionsSummary: FC<{ checkIn: ProjectCheckIn }> = ({ checkIn }) => {
  const rights = checkInPermissionOptions
    .filter(([flag]) => checkIn.permissions[flag])
    .map(([, label]) => label);
  const questionSets = checkInQuestionSetOptions
    .filter(([type]) => checkIn.permissions.accessibleQuestionSetTypes.includes(type))
    .map(([, label]) => label);

  return (
    <HoverHint
      hint={
        <div className={"space-y-2.5"}>
          <HintList title={"Rechten"} items={rights} emptyText={"Geen rechten"} />
          {questionSets.length > 0 && (
            <div className={"border-t border-slate-700 pt-2.5"}>
              <HintList title={"Vragenlijsten"} items={questionSets} />
            </div>
          )}
        </div>
      }
    >
      <Badge>
        {rights.length} {rights.length === 1 ? "recht" : "rechten"}
      </Badge>
    </HoverHint>
  );
};

/**
 * The check-ins on the projects of a company (active ones, the expired and revoked ones on request), and starting a
 * new one. Only for users who may manage users on
 * the company: the backend refuses everyone else, and a 403 would send them to the forbidden page.
 */
export const CompanyCheckIns: FC<{ company: Company; projects: Project[] }> = (props) => {
  const { users } = useApi();
  const { hasCompanyPermission } = usePermissions();
  const { listProjectCheckIns } = useApiCall();
  const canManage = hasCompanyPermission("manage_users", props.company.id);

  // Check-ins are listed per project, so fetch them for every project of the company.
  // Only the active check-ins are shown, unless the user asks for the expired (and closed) ones.
  const [showExpired, setShowExpired] = useState(false);
  const checkIns = useFetchedResource((param: { projects: Project[]; includeClosed: boolean }) =>
    Promise.all(
      param.projects.map((project) => listProjectCheckIns(project.id, param.includeClosed)),
    ).then((lists) => lists.flat()),
  );
  const projectKey = props.projects.map((project) => project.id).join(",");
  useRefreshEffect(() => {
    if (canManage) {
      checkIns.reload({ projects: props.projects, includeClosed: showExpired });
    }
  }, [canManage, projectKey, showExpired]);

  const startModal = useModal({
    title: "Check-in starten",
    body: <StartCheckInModal projects={props.projects} />,
  });

  const rows = useMemo(
    () =>
      (checkIns.resource ?? [])
        .filter((checkIn) => showExpired || checkIn.status === "active")
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()) // Newest first, across projects.
        .map((checkIn) => {
          const host = users.find((user) => user.id === checkIn.createdByUuid);
          return {
            checkIn,
            uuid: checkIn.uuid,
            projectName: props.projects.find((p) => p.id === checkIn.projectUuid)?.name ?? "-",
            hostName: host ? `${host.first_name} ${host.last_name}` : checkIn.createdByUuid,
            createdAt: checkIn.createdAt,
          };
        }),
    [checkIns.resource, users, props.projects, showExpired],
  );

  if (!canManage) {
    return null;
  }

  return (
    <>
      <div className={"flex items-center justify-between"}>
        <SectionHeader>Toegangscodes</SectionHeader>
        <div className={"mb-4"}>
          <Button
            type={"secondary"}
            size={"sm"}
            text={"Check-in starten"}
            icon={faAdd}
            disabled={props.projects.length === 0}
            onClick={() => startModal.open()}
          />
        </div>
      </div>
      <div className={"border-t -mt-2 pt-4 mb-8"}>
        {rows.length === 0 ? (
          <TablePlaceholder>
            {showExpired
              ? "Geen toegangscodes onder dit bedrijf"
              : "Geen actieve toegangscodes onder dit bedrijf"}
          </TablePlaceholder>
        ) : (
          <DataTable
            keyProperty={"uuid"}
            compact={true}
            data={rows}
            pageSize={50}
            columns={[
              {
                header: "Project",
                sort: true,
                property: "projectName",
                transform: (project, _) => {
                  return <div className={"whitespace-nowrap"}>{project}</div>;
                },
              },
              {
                header: "Aangemaakt",
                sort: true,
                property: "hostName",
                transform: (_, row) => (
                  <div>
                    <div className={"whitespace-nowrap"}>{row.hostName}</div>
                    <div className={"text-slate-600 text-xs"}>
                      {row.createdAt.toLocaleString("nl", {
                        dateStyle: "short",
                      })}
                    </div>
                  </div>
                ),
              },
              {
                header: "Toegang",
                property: "uuid",
                transform: (_, row) => (
                  <div>
                    <div className={"text-xs text-slate-600 mb-1"}>
                      {row.checkIn.grantHours} uur toegang na inchecken
                    </div>
                    <div className={"flex items-center space-x-2"}>
                      <PermissionsSummary checkIn={row.checkIn} />
                      <StatusBadge checkIn={row.checkIn} />
                    </div>
                  </div>
                ),
              },
              {
                header: "",
                property: "uuid",
                transform: (_, row) => (
                  <CheckInActions checkIn={row.checkIn} projectName={row.projectName} />
                ),
              },
            ]}
          />
        )}
        <div className={"flex justify-center pt-3"}>
          <Button
            type={"secondary"}
            size={"sm"}
            text={showExpired ? "Verberg verlopen toegangscodes" : "Toon verlopen toegangscodes"}
            onClick={() => setShowExpired(!showExpired)}
          />
        </div>
      </div>
    </>
  );
};
